import { useState, useEffect, useCallback } from 'react';
import { BREAKPOINTS } from '../styles/breakpoints';

export const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  });
  const [screenSize, setScreenSize] = useState('desktop');

  // 检查屏幕尺寸
  const checkScreenSize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    if (width < parseInt(BREAKPOINTS.sm)) {
      setScreenSize('mobile');
    } else if (width < parseInt(BREAKPOINTS.lg)) {
      setScreenSize('tablet');
    } else if (width < parseInt(BREAKPOINTS.xl)) {
      setScreenSize('desktop');
    } else {
      setScreenSize('wide');
    }

    setWindowSize({ width, height });
  }, []);

  useEffect(() => {
    // 初始化检查
    checkScreenSize();

    // 防抖处理
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkScreenSize, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [checkScreenSize]);

  // 设备类型判断
  const isMobile = windowSize.width < parseInt(BREAKPOINTS.sm);
  const isTablet = windowSize.width >= parseInt(BREAKPOINTS.sm) && windowSize.width < parseInt(BREAKPOINTS.lg);
  const isDesktop = windowSize.width >= parseInt(BREAKPOINTS.lg);
  const isLargeScreen = windowSize.width >= parseInt(BREAKPOINTS.xl);
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // 屏幕方向
  const orientation = windowSize.width > windowSize.height ? 'landscape' : 'portrait';

  // 响应式值获取器
  const getResponsiveValue = useCallback((values) => {
    if (typeof values === 'object' && values !== null) {
      return values[screenSize] || values.default || values.desktop;
    }
    return values;
  }, [screenSize]);

  return {
    windowWidth: windowSize.width, // 保持向后兼容
    windowSize,
    screenSize,
    isMobile,
    isTablet,
    isDesktop,
    isLargeScreen,
    isTouchDevice,
    orientation,
    getResponsiveValue
  };
};

/**
 * 移动端优化Hook
 */
export const useMobileOptimization = () => {
  const { isMobile, isTablet, isTouchDevice } = useResponsive();
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // 检测虚拟键盘
  useEffect(() => {
    if (!isMobile) return;

    const initialHeight = window.innerHeight;

    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDiff = initialHeight - currentHeight;

      // 如果高度减少超过150px，认为键盘打开了
      setIsKeyboardOpen(heightDiff > 150);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // 触摸优化
  const getTouchOptimizedProps = useCallback((baseProps = {}) => {
    if (!isTouchDevice) return baseProps;

    return {
      ...baseProps,
      style: {
        ...baseProps.style,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent'
      }
    };
  }, [isTouchDevice]);

  // 移动端按钮尺寸
  const getMobileButtonSize = useCallback((size = 'default') => {
    if (!isMobile) return size;

    const sizeMap = {
      small: 'default',
      default: 'large',
      large: 'large'
    };

    return sizeMap[size] || size;
  }, [isMobile]);

  return {
    isMobile,
    isTablet,
    isTouchDevice,
    isKeyboardOpen,
    getTouchOptimizedProps,
    getMobileButtonSize
  };
};